import { db } from '@/db/db';
import { put, tombstoneMany } from '@/db/repo';
import { nowMs } from '@/db/clock';
import { ATTEMPT_RETENTION_DAYS, DEFAULT_COURSE } from '@/constants/app';
import { applyResult, freshProgress } from './leitner';
import { nextLevel } from './ramp';
import type {
  Attempt,
  AttemptRollup,
  CategoryLevel,
  Difficulty,
  Item,
  NotebookEntry,
  Progress,
  StudyMode,
} from './types';

export interface AnswerInput {
  item: Pick<Item, 'id' | 'category' | 'difficulty' | 'course'>;
  correct: boolean;
  mode: StudyMode;
  /** Problem-generator seed, stored so a problem can be regenerated (Stage 6). */
  seed?: string;
}

function newId(): string {
  return crypto.randomUUID();
}

/**
 * The single path for "Got it" / "Missed it". Writes the attempt, moves the
 * item through its Leitner box, nudges the category's difficulty level, and
 * keeps the mistake notebook in step.
 */
export async function recordAnswer({ item, correct, mode, seed }: AnswerInput): Promise<void> {
  const now = nowMs();
  const course = item.course || DEFAULT_COURSE;

  const existing = await db.progress.get(item.id);
  const base = existing ?? freshProgress(item.id, course);
  await put<Progress>('progress', applyResult(base, correct, now));

  await put<Attempt>('attempts', {
    id: newId(),
    deleted: false,
    course,
    itemId: item.id,
    category: item.category,
    difficulty: item.difficulty,
    correct,
    at: now,
    mode,
    ...(seed ? { seed } : {}),
  });

  await bumpCategoryLevel(item.category, item.difficulty, correct, course);
  await updateNotebook(item.id, correct, course, now);
}

async function bumpCategoryLevel(
  category: string,
  answeredAt: Difficulty,
  correct: boolean,
  course: string,
): Promise<void> {
  const current = await db.levels.get(category);
  const level = (current?.level ?? 1) as Difficulty;

  // Only answers at the category's current level move the ramp; practising an
  // easier or harder item on purpose should not drag the level around.
  if (answeredAt !== level) {
    if (current) return;
    await put<CategoryLevel>('levels', {
      id: category,
      deleted: false,
      course,
      category,
      level,
      recentAtLevel: [],
    });
    return;
  }

  const result = nextLevel(level, current?.recentAtLevel ?? [], correct);
  await put<CategoryLevel>('levels', {
    id: category,
    deleted: false,
    course,
    category,
    level: result.level,
    recentAtLevel: result.recentAtLevel,
  });
}

/**
 * A miss opens a notebook entry. A later correct answer clears it, unless it is
 * pinned — pinned entries are kept on purpose.
 */
async function updateNotebook(
  itemId: string,
  correct: boolean,
  course: string,
  now: number,
): Promise<void> {
  const existing = await db.notebook.get(itemId);

  if (!correct) {
    await put<NotebookEntry>('notebook', {
      id: itemId,
      deleted: false,
      course,
      itemId,
      pinned: existing?.pinned ?? false,
      addedAt: existing?.addedAt ?? now,
      clearedAt: null,
      ...(existing?.note ? { note: existing.note } : {}),
    });
    return;
  }

  if (existing && !existing.deleted && existing.clearedAt === null && !existing.pinned) {
    await put<NotebookEntry>('notebook', { ...existing, clearedAt: now });
  }
}

export async function setNotebookNote(itemId: string, note: string): Promise<void> {
  const existing = await db.notebook.get(itemId);
  if (!existing) return;
  await put<NotebookEntry>('notebook', { ...existing, note });
}

export async function setNotebookPinned(itemId: string, pinned: boolean): Promise<void> {
  const existing = await db.notebook.get(itemId);
  if (!existing) return;
  await put<NotebookEntry>('notebook', { ...existing, pinned });
}

/**
 * Raw attempts older than 90 days collapse into per-category per-day totals, so
 * the weak-spot history survives without the table growing without bound.
 */
export async function rollUpOldAttempts(now = Date.now()): Promise<number> {
  const cutoff = now - ATTEMPT_RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const old = (await db.attempts.where('at').below(cutoff).toArray()).filter((a) => !a.deleted);
  if (old.length === 0) return 0;

  const buckets = new Map<string, { category: string; day: string; attempts: number; correct: number; course: string }>();
  for (const attempt of old) {
    const day = new Date(attempt.at).toISOString().slice(0, 10);
    const key = `${attempt.category}|${day}`;
    const bucket = buckets.get(key) ?? {
      category: attempt.category,
      day,
      attempts: 0,
      correct: 0,
      course: attempt.course,
    };
    bucket.attempts += 1;
    if (attempt.correct) bucket.correct += 1;
    buckets.set(key, bucket);
  }

  for (const [key, bucket] of buckets) {
    const existing = await db.rollups.get(key);
    await put<AttemptRollup>('rollups', {
      id: key,
      deleted: false,
      course: bucket.course,
      category: bucket.category,
      day: bucket.day,
      attempts: (existing?.attempts ?? 0) + bucket.attempts,
      correct: (existing?.correct ?? 0) + bucket.correct,
    });
  }

  // Tombstoned rather than dropped, so the other device removes them too
  // instead of pushing them back on the next pull.
  await tombstoneMany('attempts', old.map((a) => a.id));
  return old.length;
}
